// ===============================================
// File: src/components/ExportAccountingExcel.jsx

import * as XLSX from "xlsx";

/* ---------- helpers ---------- */
function aoaToSheet(aoa, opts = {}) {
  const ws = XLSX.utils.aoa_to_sheet(aoa, opts);
  // Auto width by max content length
  const colWidths = aoa.reduce((acc, row) => {
    row.forEach((val, i) => {
      const len = String(val ?? "").length;
      acc[i] = Math.max(acc[i] || 10, len + 2);
    });
    return acc;
  }, []);
  ws["!cols"] = colWidths.map((wch, i) => {
    // Wider for address, reasonable caps elsewhere
    const cap = i === 1 ? 72 : 28;
    return { wch: Math.min(Math.max(wch, i === 1 ? 24 : 12), cap) };
  });
  return ws;
}

function formatRange(ws, range, numFmt) {
  const r = XLSX.utils.decode_range(range);
  for (let R = r.s.r; R <= r.e.r; ++R) {
    for (let C = r.s.c; C <= r.e.c; ++C) {
      const ref = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[ref];
      if (!cell) continue;
      if (typeof cell.v === "number" || typeof cell.w === "number") {
        cell.t = "n";
        cell.z = numFmt;
      }
    }
  }
}

function freezeTop(ws, rows = 1) {
  ws["!freeze"] = {
    xSplit: 0,
    ySplit: rows,
    topLeftCell: "A2",
    activePane: "bottomLeft",
  };
}

/* ---------- main export ---------- */
export function exportAccountingExcel({
  summary,
  facilities,
  from,
  to,
  facilityIds = [],
}) {
  const wb = XLSX.utils.book_new();

  // Workbook metadata (shows in file properties)
  wb.Props = {
    Title: "Accounting Export",
    Subject: "Landlord Accounting",
    Author: "Landlord Portal",
    CreatedDate: new Date(),
  };

  /* ----- SUMMARY ----- */
  const title = `Accounting Summary (${from} → ${to})`;
  const summaryRows = [
    [title, ""],
    ["Date Range", `${from} → ${to}`],
    ["Facilities Selected", facilityIds.join(", ") || "—"],
    [""],
    ["Section", "Value"],
    ["Total Net Remit", summary?.netRemit ?? 0],
    ["Net Transient", summary?.netTransient ?? 0],
    ["Reservations", summary?.reservations ?? 0],
    [""],
    ["— Total —", ""],
    ["Gross Remit", summary?.grossRemit ?? 0],
    ["Refunds", summary?.refunds ?? 0],
    ["Manual Adj.", summary?.manualAdj ?? 0],
    ["Total After Adj.", summary?.totalAfterAdj ?? 0],
    ["Total Taxes", summary?.totalTaxes ?? 0],
    [""],
    ["— Transient —", ""],
    ["Gross Remit", summary?.tGrossRemit ?? 0],
    ["Refunds", summary?.tRefunds ?? 0],
    ["Manual Adj.", summary?.tManualAdj ?? 0],
    ["Total After Adj.", summary?.tTotalAfterAdj ?? 0],
    ["Total Taxes", summary?.tTotalTaxes ?? 0],
    [""],
    ["— Monthly —", ""],
    ["Has Monthly", summary?.hasMonthly ? "Yes" : "No"],
    ["Gross Remit", summary?.mGrossRemit ?? 0],
    ["Refunds", summary?.mRefunds ?? 0],
    ["Manual Adj.", summary?.mManualAdj ?? 0],
    ["Total After Adj.", summary?.mTotalAfterAdj ?? 0],
    ["Total Taxes", summary?.mTotalTaxes ?? 0],
    ["Net Monthly", summary?.mNet ?? 0],
  ];
  const wsSummary = aoaToSheet(summaryRows);
  // Merge A1:B1 for big title
  wsSummary["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
  // Number formats
  formatRange(wsSummary, "B6:B7", "$#,##0.00");
  formatRange(wsSummary, "B10:B14", "$#,##0.00");
  formatRange(wsSummary, "B16:B20", "$#,##0.00");
  formatRange(wsSummary, "B23:B28", "$#,##0.00");
  // Row heights: taller title
  wsSummary["!rows"] = [{ hpt: 22 }];
  freezeTop(wsSummary, 1);
  XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

  /* ----- FACILITIES ----- */
  const facHeader = [
    "Facility ID",
    "Facility Address",
    "Transient",
    "Monthly",
    "Refunds",
    "Manual Adj.",
    "Gross",
    "Net Revenue",
  ];

  const facData = (facilities || []).map((f) => [
    f.id,
    f.address,
    Number(f.transient || 0),
    Number(f.monthly || 0),
    Number(f.refunds || 0),
    Number(f.adjustments || 0),
    Number(f.gross || 0),
    Number(f.net || 0),
  ]);

  // Totals row with formulas
  const startRow = 2; // data starts at row 2 (1-based)
  const endRow = startRow + Math.max(facData.length, 0) - 1;
  const totalsRow =
    facData.length > 0
      ? [
          "Totals",
          "",
          `=SUM(C${startRow}:C${endRow})`,
          `=SUM(D${startRow}:D${endRow})`,
          `=SUM(E${startRow}:E${endRow})`,
          `=SUM(F${startRow}:F${endRow})`,
          `=SUM(G${startRow}:G${endRow})`,
          `=SUM(H${startRow}:H${endRow})`,
        ]
      : ["Totals", "", 0, 0, 0, 0, 0, 0];

  const facRows = [facHeader, ...facData, totalsRow];
  const wsFacilities = aoaToSheet(facRows);
  // Freeze header + autofilter
  freezeTop(wsFacilities, 1);
  wsFacilities["!autofilter"] = {
    ref: `A1:H${facRows.length}`,
  };
  // Wrap text for address column
  wsFacilities["!cols"][1] = {
    ...(wsFacilities["!cols"][1] || {}),
    wch: Math.max(wsFacilities["!cols"][1]?.wch || 24, 36),
  };
  wsFacilities["!rows"] = [{ hpt: 20 }]; // header taller

  // Formats: integer for Transient; currency for others
  if (facData.length > 0) {
    formatRange(wsFacilities, `C2:C${endRow + 1}`, "0");
    ["D", "E", "F", "G", "H"].forEach((col) =>
      formatRange(wsFacilities, `${col}2:${col}${endRow + 1}`, "$#,##0.00")
    );
  }
  // Totals row format (last row)
  const last = facRows.length;
  formatRange(wsFacilities, `C${last}:H${last}`, "$#,##0.00");

  XLSX.utils.book_append_sheet(wb, wsFacilities, "Facilities");

  /* ----- ABOUT ----- */
  const wsAbout = aoaToSheet([
    ["About"],
    [""],
    ["Generated", new Date().toISOString()],
    ["Date Range", `${from} → ${to}`],
    ["Facilities Count", facilityIds.length],
    ["Facilities", facilityIds.join(", ") || "—"],
    [""],
    ["Notes"],
    [
      "All currency values are in dollars and reflect the selected range and facilities at export time.",
    ],
  ]);
  XLSX.utils.book_append_sheet(wb, wsAbout, "About");

  // Filename
  XLSX.writeFile(wb, `Accounting_${from}_to_${to}.xlsx`);
}

/** Button wrapper (unchanged API) */
export default function ExportAccountingExcelButton(props) {
  const {
    summary,
    facilities,
    from,
    to,
    facilityIds,
    disabled,
    className = "",
    children,
  } = props;
  const onClick = () =>
    exportAccountingExcel({ summary, facilities, from, to, facilityIds });
  return (
    <button onClick={onClick} disabled={disabled} className={className}>
      {children || "Export"}
    </button>
  );
}
